import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Image as ImageIcon, Upload, Trash2, Pencil, ExternalLink, Download, Loader2, FolderOpen, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogBody as DialogBody,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogFooter as DialogFooter,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
} from "@/components/ui/responsive-dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DatePicker } from "@/components/DatePicker";
import { EmptyState } from "@/components/EmptyState";
import { fileUrl } from "@/lib/files";
import { formatDate, todayISO } from "@/lib/format";
import { DOC_CATEGORY, DOC_CATEGORY_OPTIONS, ACCEPT_DOCS, isPdf, formatBytes, uploadMachineDocument } from "@/lib/documents";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

const CategoryBadge = ({ category }) => (
  <Badge variant="secondary" className={cn("gap-1 text-[10px] font-semibold", category === "aarskontroll" && "bg-[#0B0D10] text-white hover:bg-[#0B0D10] dark:bg-white dark:text-[#0B0D10]")} data-testid={`doc-category-${category}`}>
    {category === "aarskontroll" && <ShieldCheck className="h-3 w-3" />}
    {DOC_CATEGORY[category] || "Anna"}
  </Badge>
);

const DocIcon = ({ doc }) => (
  <span className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-lg bg-muted text-muted-foreground">
    {isPdf(doc) ? <FileText className="h-5 w-5" /> : doc.thumb_url ? <img src={fileUrl(doc.id, { size: "thumb" })} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-5 w-5" />}
  </span>
);

/**
 * MachineDocuments – arkiv på maskina: årskontroll-rapportar frå sertifiseringsorganet, sertifikat, manualar.
 * documents: public file records (kind=document). onChange(documents) after upload/edit/delete.
 */
export const MachineDocuments = ({ machineId, documents = [], onChange, compact = false }) => {
  const inputRef = useRef(null);
  const [pending, setPending] = useState(null); // File chosen, awaiting metadata
  const [meta, setMeta] = useState({ title: "", category: "aarskontroll", doc_date: todayISO() });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!isPdf(f) && !f.type.startsWith("image/")) return toast.error("Berre PDF eller bilete kan lastast opp.");
    if (f.size > 25 * 1024 * 1024) return toast.error("Fila er for stor (maks 25 MB).");
    setPending(f);
    setMeta({ title: (f.name || "").replace(/\.[^.]+$/, ""), category: "aarskontroll", doc_date: todayISO() });
  };

  const upload = async () => {
    if (!pending) return;
    setBusy(true);
    setProgress(0);
    try {
      const r = await uploadMachineDocument(machineId, pending, { title: meta.title, category: meta.category, docDate: meta.doc_date, onProgress: setProgress });
      onChange?.(r.documents);
      toast.success("Dokumentet er lagra på maskina.");
      setPending(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikkje laste opp dokumentet.");
    } finally {
      setBusy(false);
    }
  };

  const saveMeta = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/machines/${machineId}/documents/${editing.id}`, { title: editing.title, category: editing.category, doc_date: editing.doc_date || "" });
      onChange?.(r.data.documents);
      toast.success("Dokument oppdatert.");
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikkje lagre.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      const r = await api.delete(`/machines/${machineId}/documents/${toDelete.id}`);
      onChange?.(r.data.documents);
      toast.success("Dokument fjerna.");
    } catch (e) {
      toast.error("Kunne ikkje slette dokumentet.");
    } finally {
      setToDelete(null);
    }
  };

  const open = (d) => window.open(fileUrl(d.id), "_blank", "noopener");
  const download = (d) => {
    const a = document.createElement("a");
    a.href = fileUrl(d.id);
    a.download = d.original_filename || `${d.title || "dokument"}.${isPdf(d) ? "pdf" : "jpg"}`;
    a.click();
  };

  const MetaFields = ({ value, onChange: set, testid }) => (
    <>
      <div className="space-y-1.5">
        <Label className="text-sm font-semibold">Tittel</Label>
        <Input className="h-11" value={value.title || ""} onChange={(e) => set({ ...value, title: e.target.value })} placeholder="t.d. Årskontroll 2026 – Kiwa" data-testid={`${testid}-title`} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-sm font-semibold">Kategori</Label>
          <Select value={value.category} onValueChange={(v) => set({ ...value, category: v })}>
            <SelectTrigger className="h-11" data-testid={`${testid}-category`}><SelectValue /></SelectTrigger>
            <SelectContent>{DOC_CATEGORY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testid}-category-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-semibold">Dato</Label>
          <DatePicker value={value.doc_date || ""} onChange={(d) => set({ ...value, doc_date: d })} testid={`${testid}-date`} />
        </div>
      </div>
    </>
  );

  return (
    <>
      <Card className="shadow-sm" data-testid="machine-documents">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div>
            <CardTitle className="flex items-center gap-2 font-heading text-base font-bold"><FolderOpen className="h-4 w-4 text-primary" /> Dokument</CardTitle>
            {!compact && <p className="mt-0.5 text-xs text-muted-foreground">Årskontroll-rapportar frå sertifiseringsorganet, sertifikat og manualar – samla på maskina.</p>}
          </div>
          <Button size="sm" onClick={() => inputRef.current?.click()} data-testid="doc-upload-button"><Upload /> Last opp</Button>
          <input ref={inputRef} type="file" accept={ACCEPT_DOCS} className="hidden" onChange={pick} data-testid="doc-file-input" />
        </CardHeader>
        <CardContent className="pt-0">
          {documents.length === 0 ? (
            <EmptyState icon={FolderOpen} title="Ingen dokument enno" description="Last opp kontrollrapporten frå sertifiseringsorganet (PDF) eller bilete av sertifikatet, så ligg det alltid på maskina." action={<Button variant="outline" onClick={() => inputRef.current?.click()}><Upload /> Last opp dokument</Button>} />
          ) : (
            <ul className="divide-y divide-border rounded-lg border" data-testid="doc-list">
              {documents.map((d) => (
                <li key={d.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`doc-row-${d.id}`}>
                  <DocIcon doc={d} />
                  <button type="button" onClick={() => open(d)} className="min-w-0 flex-1 text-left" data-testid={`doc-open-${d.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-semibold">{d.title || d.original_filename || "Dokument"}</span>
                      <CategoryBadge category={d.category} />
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {[d.doc_date ? formatDate(d.doc_date) : null, isPdf(d) ? "PDF" : "Bilete", formatBytes(d.size), d.uploaded_by_name].filter(Boolean).join(" · ")}
                    </div>
                  </button>
                  <div className="flex shrink-0 gap-0.5">
                    <Button size="iconSm" variant="ghost" onClick={() => open(d)} aria-label="Opne" data-testid={`doc-view-${d.id}`}><ExternalLink className="h-4 w-4" /></Button>
                    <Button size="iconSm" variant="ghost" className="hidden sm:inline-flex" onClick={() => download(d)} aria-label="Last ned" data-testid={`doc-download-${d.id}`}><Download className="h-4 w-4" /></Button>
                    <Button size="iconSm" variant="ghost" onClick={() => setEditing({ id: d.id, title: d.title || "", category: d.category || "anna", doc_date: d.doc_date || "" })} aria-label="Rediger" data-testid={`doc-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="iconSm" variant="ghost" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setToDelete(d)} aria-label="Slett" data-testid={`doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Upload metadata */}
      <Dialog open={!!pending} onOpenChange={(o) => !o && !busy && setPending(null)}>
        <DialogContent className="sm:max-w-md" data-testid="doc-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Last opp dokument</DialogTitle>
            <DialogDescription className="truncate">{pending?.name} · {formatBytes(pending?.size)}</DialogDescription>
          </DialogHeader>
          <DialogBody>
            <MetaFields value={meta} onChange={setMeta} testid="doc-upload" />
            {busy && <div className="h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary transition-[width] duration-150" style={{ width: `${progress}%` }} /></div>}
          </DialogBody>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setPending(null)} disabled={busy} data-testid="doc-upload-cancel">Avbryt</Button>
            <Button onClick={upload} disabled={busy} data-testid="doc-upload-confirm">{busy ? <Loader2 className="animate-spin" /> : <Upload />} Last opp</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit metadata */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md" data-testid="doc-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Rediger dokument</DialogTitle>
            <DialogDescription>Endre tittel, kategori eller dato.</DialogDescription>
          </DialogHeader>
          <DialogBody>{editing && <MetaFields value={editing} onChange={setEditing} testid="doc-edit" />}</DialogBody>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setEditing(null)} data-testid="doc-edit-cancel">Avbryt</Button>
            <Button onClick={saveMeta} disabled={busy} data-testid="doc-edit-save">{busy && <Loader2 className="animate-spin" />} Lagre</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette dokument?</AlertDialogTitle>
            <AlertDialogDescription>«{toDelete?.title || toDelete?.original_filename}» blir fjerna frå maskina.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="doc-delete-confirm">Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
